import React, { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import type SvgIcon from 'LuxKit/SvgIcon'
import { useAppSelector } from 'hooks/reduxHooks'
import ContactContext from 'contexts/contactContext'
import { getBenefitCtaConfig } from 'luxPlus/lib/utils'
import { SUPPORTED_BENEFITS_ICONS } from '../LuxPlusBenefitIcon'
import LuxPlusSubscriptionTsAndCs from '../LuxPlusSubscriptionTsAndCs'
import useScrollToId from 'hooks/useScrollToId'
import useLuxPlusLandingActions from 'luxPlus/hooks/useLuxPlusLandingActions'
import useShouldShowMembershipCta from 'luxPlus/hooks/useShouldShowMembershipCta'
import useLandingBenefits, { type LandingBenefitEntry, type LandingBenefitType, type FrontendOnlyBenefitType } from 'luxPlus/hooks/useLandingBenefits'
import { getCanRedeemLuxPlusBenefits, getIsFreePreviewLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import LineTagAltIcon from 'LuxKit/Icons/line/LineTagAltIcon'
import LineBellRingIcon from 'LuxKit/Icons/line/LineBellRingIcon'
import LuxPlusLandingNavigationContext from 'luxPlus/contexts/LuxPlusLandingTabContext'

const SectionWrapper = styled.div`
  padding: ${rem(40)} 0;
  background-color: ${props => props.theme.palette.neutral.default.seven};

  ${mediaQueryUp.tablet} {
    padding: ${rem(60)} 0;
  }
`

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  row-gap: ${rem(12)};
  column-gap: ${rem(28)};
  width: 100%;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }
`

const BenefitTile = styled.button<{ $interactive?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${rem(16)};
  padding: ${rem(20)};
  background: ${props => props.theme.palette.neutral.default.eight};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.M};
  cursor: ${props => props.$interactive ? 'pointer' : 'default'};
  text-align: left;
  width: 100%;
  transition: background-color 0.15s;

  ${props => props.$interactive && `
    &:hover {
      background-color: ${props.theme.palette.neutral.default.seven};
    }
  `}

  ${mediaQueryUp.tablet} {
    padding: ${rem(24)};
  }
`

const BenefitContent = styled.div`
  flex: 1;
  min-width: 0;
`

const CTASection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    justify-content: center;
  }
`

const FRONTEND_ONLY_BENEFIT_ICONS: Record<FrontendOnlyBenefitType, typeof SvgIcon> = {
  exclusive_partner_offers: LineTagAltIcon,
  weekly_member_discounts: LineBellRingIcon,
}

const LANDING_BENEFIT_ICONS: Record<LandingBenefitType, typeof SvgIcon> = {
  ...SUPPORTED_BENEFITS_ICONS,
  ...FRONTEND_ONLY_BENEFIT_ICONS,
}

interface BenefitTileItemProps {
  entry: LandingBenefitEntry
}

function BenefitTileItem({ entry }: BenefitTileItemProps) {
  const intl = useIntl()
  const { navigateToPartnerOffers } = useContext(LuxPlusLandingNavigationContext)
  const ctaConfig = entry.frontendOnly ? undefined : getBenefitCtaConfig(entry.type)
  const supportContact = useContext(ContactContext)
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const { hasLoadedScrollElement, onScrollToId } = useScrollToId(ctaConfig?.scrollTarget ?? '', -100)

  const handleClick = useCallback(() => {
    if (entry.type === 'exclusive_partner_offers') {
      navigateToPartnerOffers()
      return
    }
    if (ctaConfig?.scrollTarget) {
      onScrollToId()
      if (ctaConfig.filterCategory) {
        // Dispatch for filter auto-select
        const target = document.getElementById(ctaConfig.scrollTarget)
        target?.dispatchEvent(new CustomEvent('luxplus-filter-select', {
          detail: { category: ctaConfig.filterCategory },
        }))
      }
    }
  }, [entry.type, navigateToPartnerOffers, onScrollToId, ctaConfig?.scrollTarget, ctaConfig?.filterCategory])

  const hasPhoneNumber = entry.type === 'priority_support' && !!supportContact.local.number && canRedeemLuxPlusBenefits
  const prioritySupportDescription = hasPhoneNumber && entry.description ? intl.formatMessage(entry.description, { phoneNumber: supportContact.local.humanReadable }) : undefined

  const title = intl.formatMessage(entry.title)
  // use phone number or hide description if priority support
  const description = entry.type === 'priority_support'
    ? prioritySupportDescription
    : (entry.description ? intl.formatMessage(entry.description) : undefined)
  const Icon = LANDING_BENEFIT_ICONS[entry.type]

  const isPartnerOffersTile = entry.type === 'exclusive_partner_offers'
  const phoneTelUri = entry.type === 'priority_support' && supportContact.local.number && canRedeemLuxPlusBenefits
    ? `tel:${supportContact.local.number}`
    : undefined
  const linkTo = phoneTelUri ?? ctaConfig?.path
  const hasAction = isPartnerOffersTile || linkTo || (ctaConfig?.scrollTarget && hasLoadedScrollElement)

  return (
    <BenefitTile
      as={linkTo ? 'a' : 'button'}
      href={linkTo}
      onClick={hasAction ? handleClick : undefined}
      $interactive={!!hasAction}
    >
      <Icon colour="lux-plus" size="L" />
      <BenefitContent>
        <BodyText variant="medium" weight="bold">{title}</BodyText>
        {!!description && (
          <BodyText variant="medium" colour="neutral-three">{description}</BodyText>
        )}
      </BenefitContent>
      {!!hasAction && <LineAngleRightIcon size="S" colour="neutral-three" />}
    </BenefitTile>
  )
}

function LuxPlusLandingBenefitsList() {
  const intl = useIntl()
  const visibleBenefits = useLandingBenefits()
  const { handleBecomeMember, handleStartFreePreview } = useLuxPlusLandingActions()
  const showCta = useShouldShowMembershipCta()
  const isPreview = useAppSelector(getIsFreePreviewLuxPlusMember)

  if (!visibleBenefits.length) return null

  return (
    <SectionWrapper>
      <LayoutContainer>
        <Group direction="vertical" gap={24} horizontalAlign="center">
          <Group direction="vertical" gap={8} horizontalAlign="center">
            <HeadingTextBlock variant="heading2" align="center">
              {intl.formatMessage(
                {
                  id: 'ECdHKUn3hs-jXeHAxW9i',
                  defaultMessage: '{programName} benefits',
                  description: 'Heading for LuxPlus benefits list section',
                },
                { programName: LUXURY_PLUS.PROGRAM_NAME },
              )}
            </HeadingTextBlock>
            <BodyTextBlock variant="large" colour="neutral-one" align="center">
              {intl.formatMessage(
                {
                  id: 'c1CynT_JDDFc58DHVOSx',
                  defaultMessage: 'All the ways {programName} helps you save and travel better',
                  description: 'Subheading for LuxPlus benefits list section',
                },
                { programName: LUXURY_PLUS.PROGRAM_NAME },
              )}
            </BodyTextBlock>
          </Group>

          <BenefitsGrid>
            {visibleBenefits.map(entry => (
              <BenefitTileItem
                key={entry.type}
                entry={entry}
              />
            ))}
          </BenefitsGrid>

          {showCta && <>
            <CTASection>
              <TextButton kind="primary" onClick={handleBecomeMember}>
                {intl.formatMessage({
                  id: '6ajbFZSXd33DczR2endE',
                  defaultMessage: 'Become a member',
                  description: 'CTA button to join LuxPlus on benefits list section',
                })}
              </TextButton>
              {!isPreview && (
                <TextButton kind="secondary" onClick={handleStartFreePreview}>
                  {intl.formatMessage({
                    id: 'di7K46pwDVuApxrcDjdX',
                    defaultMessage: 'Start 28-day free preview',
                    description: 'CTA button to start free preview on benefits list section',
                  })}
                </TextButton>
              )}
            </CTASection>

            <LuxPlusSubscriptionTsAndCs />
          </>}
        </Group>
      </LayoutContainer>
    </SectionWrapper>
  )
}

export default LuxPlusLandingBenefitsList
